import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { QuillPenLine } from '@mingcute/react';

import { cn } from '../../lib/cn';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';

export interface AIConfidenceBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Confidence value in [0, 1]. */
  value: number;
  /** Optional explanation text shown in the tooltip. */
  explanation?: string;
}

function toneFor(v: number): { className: string; key: string } {
  if (v >= 0.9) {
    return {
      className:
        'bg-[var(--color-success-light)] text-[var(--color-success-dark)] border-[var(--color-success)]',
      key: 'high',
    };
  }
  if (v >= 0.7) {
    return {
      className:
        'bg-[var(--color-warning-light)] text-[var(--color-warning-dark)] border-[var(--color-warning)]',
      key: 'medium',
    };
  }
  return {
    className:
      'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)] border-[var(--color-danger)]',
    key: 'low',
  };
}

export const AIConfidenceBadge = React.forwardRef<HTMLSpanElement, AIConfidenceBadgeProps>(
  ({ className, value, explanation, ...props }, ref) => {
    const { t } = useTranslation();
    const clamped = Math.max(0, Math.min(1, value));
    const pct = Math.round(clamped * 100);
    const tone = toneFor(clamped);
    const tipDefault = t(`ai.confidence.${tone.key}`, {
      defaultValue:
        tone.key === 'high'
          ? 'High confidence'
          : tone.key === 'medium'
            ? 'Medium confidence — review recommended'
            : 'Low confidence — manual review required',
    });
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              ref={ref}
              className={cn(
                'inline-flex items-center gap-1 h-5 px-1.5 rounded-[var(--radius-sm)] border text-[10px] font-medium tabular-nums',
                tone.className,
                className,
              )}
              {...props}
            >
              <QuillPenLine size={12} aria-hidden />
              {pct}%
            </span>
          </TooltipTrigger>
          <TooltipContent>{explanation ?? tipDefault}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
AIConfidenceBadge.displayName = 'AIConfidenceBadge';
